/**
 * EulaModal.tsx
 *
 * Standard React EULA modal using HTML/CSS
 * Replaces react-native-web version
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';

interface EulaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EulaModal: React.FC<EulaModalProps> = ({ isOpen, onClose }) => {
  const { currentTheme: colors } = useTheme();

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="End User License Agreement">
      <div style={{ color: colors.textMain }}>
        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          1. Acceptance of Terms
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          By accessing or using the Via Logos application ("App"), you agree to be bound by this End User License Agreement (EULA). If you do not agree to these terms, please do not use the App.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          2. License Grant
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          You are granted a limited, non-exclusive, non-transferable license to use the App for personal, non-commercial purposes. You may not modify, reverse engineer, or distribute the App.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          3. User Content
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          You retain ownership of any content you create within the App. By using the App, you grant us a license to store, process, and transmit your content solely to provide the App's services.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          4. AI-Generated Content
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          The App uses artificial intelligence to generate narrative content. Such content is provided "as is" and may not always be accurate, appropriate, or suitable for all audiences. You use AI-generated content at your own risk.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          5. Disclaimers
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          6. Limitation of Liability
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the App.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          7. Termination
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          We reserve the right to terminate or suspend your access to the App at any time, with or without cause, with or without notice.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          8. Governing Law
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          This EULA shall be governed by and construed in accordance with applicable laws.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          9. Changes to Terms
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          We reserve the right to modify this EULA at any time. Continued use of the App after such changes constitutes acceptance of the new terms.
        </div>

        <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 16, marginBottom: 8, color: colors.primary }}>
          10. Contact
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', marginBottom: 8 }}>
          For questions about this EULA, please contact us through the App's support channels.
        </div>

        <div style={{ marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)', borderTopStyle: 'solid' }}>
          <div style={{ fontSize: 12, fontStyle: 'italic', color: colors.textMuted }}>
            Last Updated: December 26, 2025
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px 16px',
            marginTop: 24,
            borderRadius: 12,
            backgroundColor: colors.primary,
            border: 'none',
            color: colors.background,
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          I Accept
        </button>
      </div>
    </ScrollableModal>
  );
};

export { EulaModal };
